import React, { useState } from 'react';
import { HandshakeIcon, CurrencyDollarIcon, MegaphoneIcon, UserGroupIcon, ChartBarIcon, LightningBoltIcon, WhatsAppIcon, PhoneIcon, EmailIcon } from '../components/Icons';
import api from '../services/api';
import SEO from '../components/SEO';

const B2B = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    businessType: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Submit to backend API
      await api.createB2bInquiry({
        company_name: formData.companyName,
        contact_person: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        business_type: formData.businessType,
        message: formData.message || ''
      });

      // On success, also open WhatsApp with the message
      const whatsappMessage = `B2B Partnership Inquiry%0A%0ACompany: ${formData.companyName}%0AContact Person: ${formData.contactPerson}%0AEmail: ${formData.email}%0APhone: ${formData.phone}%0ABusiness Type: ${formData.businessType}%0AMessage: ${formData.message}`;
      window.open(`https://wa.me/917718838808?text=${whatsappMessage}`, '_blank');
      
      // Reset form
      setFormData({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        businessType: '',
        message: ''
      });
      
      setIsSubmitted(true);
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (err) {
      console.error('Error submitting B2B inquiry:', err);
      setError('Failed to submit your request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: HandshakeIcon,
      title: 'Partnership Benefits',
      description: 'Exclusive rates and special offers for B2B partners'
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Competitive Pricing',
      description: 'Best-in-market pricing with attractive commission structures'
    },
    {
      icon: MegaphoneIcon,
      title: 'Marketing Support',
      description: 'Marketing materials and promotional support for your business'
    },
    {
      icon: UserGroupIcon,
      title: 'Dedicated Support',
      description: 'Dedicated account manager for personalized assistance'
    },
    {
      icon: ChartBarIcon,
      title: 'Growth Opportunities',
      description: 'Scale your business with our diverse travel packages'
    },
    {
      icon: LightningBoltIcon,
      title: 'Quick Response',
      description: 'Fast turnaround times for bookings and inquiries'
    }
  ];

  return (
    <div className="flex-1">
      <SEO
        title="B2B Partnership"
        description="Partner with Happy Yatra Travels for B2B travel solutions. Competitive pricing, dedicated support, marketing materials, and growth opportunities for travel agents and businesses."
        keywords="B2B travel, travel agency partnership, wholesale travel, travel agent partnership, B2B tour packages"
        canonical="/b2b"
      />
      <section className="bg-gradient-to-r from-primary-orange to-orange-600 text-white py-16 md:py-20 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">B2B Partnership Program</h1>
          <p className="text-xl md:text-2xl text-white/90">Partner with Happy Yatra Travels and grow your travel business</p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Partner With Us?</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              At Happy Yatra Travels, we value our B2B partners and are committed to providing 
              you with the best travel solutions to serve your customers. Join our network of 
              trusted partners and unlock exclusive benefits, competitive pricing, and dedicated 
              support to help your business thrive.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">Partnership Benefits</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div 
                  key={index} 
                  className="bg-white p-8 rounded-2xl text-center transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-xl border border-gray-100"
                >
                  <div className="flex justify-center mb-4">
                    <IconComponent className="w-16 h-16 text-primary-orange" />
                  </div>
                  <h3 className="text-xl font-semibold text-primary-blue mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-gray-50 to-white p-8 md:p-12 rounded-2xl shadow-xl border border-gray-100">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Become a Partner</h2>
              <p className="text-lg text-gray-600">Fill out the form below and we'll get back to you within 24 hours</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block text-gray-800 font-semibold mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-blue focus:outline-none transition-colors"
                    placeholder="Enter your company name"
                  />
                </div>

                <div>
                  <label htmlFor="contactPerson" className="block text-gray-800 font-semibold mb-2">
                    Contact Person *
                  </label>
                  <input
                    type="text"
                    id="contactPerson"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-blue focus:outline-none transition-colors"
                    placeholder="Your name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-gray-800 font-semibold mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-blue focus:outline-none transition-colors"
                    placeholder="your.email@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-gray-800 font-semibold mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-blue focus:outline-none transition-colors"
                    placeholder="+91 77188 38808"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="businessType" className="block text-gray-800 font-semibold mb-2">
                  Business Type *
                </label>
                <select
                  id="businessType"
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-blue focus:outline-none transition-colors"
                >
                  <option value="">Select business type</option>
                  <option value="travel-agency">Travel Agency</option>
                  <option value="corporate">Corporate Travel</option>
                  <option value="online-platform">Online Platform</option>
                  <option value="hotel">Hotel/Resort</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-gray-800 font-semibold mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-blue focus:outline-none transition-colors resize-vertical"
                  placeholder="Tell us about your business and partnership requirements..."
                ></textarea>
              </div>

              {isSubmitted && (
                <div className="bg-green-500 text-white p-4 rounded-xl text-center font-semibold">
                  ✓ Partnership request submitted successfully! We'll get back to you within 24 hours.
                </div>
              )}

              {error && (
                <div className="bg-red-500 text-white p-4 rounded-xl text-center font-semibold">
                  {error}
                </div>
              )}

              <button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-primary-orange text-white rounded-xl font-semibold hover:bg-primary-blue transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Partnership Request'}
              </button>
            </form>

            <div className="mt-12 pt-8 border-t-2 border-gray-200 text-center">
              <p className="text-lg text-gray-600 mb-6">Prefer to contact us directly?</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="https://wa.me/917718838808?text=Hi, I'm interested in B2B partnership"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-[#25d366] text-white rounded-xl font-semibold hover:bg-[#20ba5a] transition-all duration-300"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                  WhatsApp Us
                </a>
                <a 
                  href="tel:+917718838808" 
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-blue text-white rounded-xl font-semibold hover:bg-dark-blue transition-all duration-300"
                >
                  <PhoneIcon className="w-5 h-5" />
                  Call Us
                </a>
                <a 
                  href="mailto:info@happyyatratravels.in" 
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-800 text-white rounded-xl font-semibold hover:bg-gray-900 transition-all duration-300"
                >
                  <EmailIcon className="w-5 h-5" />
                  Email Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default B2B;
